import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { Observable, firstValueFrom } from 'rxjs';
import * as vo from '../../../core/model/server/vo/_index';

@Injectable({
  providedIn: 'root'
})
export class ProfanityFilterService {

  constructor(
    private apiService: ApiService,
  ) { }

  // Check for duplicate words between restrict and allow inputs
  findDuplicateWords(restrictInput: string, allowInput: string): string[] {
    const restrictWords = this.parseWordsFromInput(restrictInput);
    const allowWords = this.parseWordsFromInput(allowInput);
    
    // Convert to lowercase for case-insensitive comparison
    const restrictWordsLower = restrictWords.map(w => w.toLowerCase());
    const allowWordsLower = allowWords.map(w => w.toLowerCase());
    
    // Find duplicates
    const duplicates = restrictWords.filter((word, index) => 
      allowWordsLower.includes(restrictWordsLower[index])
    );
    
    return duplicates;
  }

  // Process add/remove operations with validation
  async processWords(
    restrictInput: string, 
    allowInput: string
  ): Promise<{
    success: boolean;
    restrictCount: number;
    allowCount: number;
    errors: string[];
  }> {
    const restrictWords = this.parseWordsFromInput(restrictInput);
    const allowWords = this.parseWordsFromInput(allowInput);
    const errors: string[] = [];
    let restrictCount = 0;
    let allowCount = 0;

    // Validate: at least one field must have input
    if (restrictWords.length === 0 && allowWords.length === 0) {
      return {
        success: false,
        restrictCount: 0,
        allowCount: 0,
        errors: ['Please enter at least one word to add or remove']
      };
    }

    // Add restricted words (if any)
    if (restrictWords.length > 0) {
      try {
        const response = await this.addProfanityWords(restrictWords);
        if (this.isSuccessResponse(response)) {
          restrictCount = restrictWords.length;
        } else {
          errors.push(response?.message || response?.error?.message || 'Failed to add restricted words');
        }
      } catch (error: any) {
        errors.push(this.getErrorMessage(error, 'Failed to add restricted words'));
      }
    }

    // Remove allowed words (if any)
    if (allowWords.length > 0) {
      try {
        const response = await this.removeProfanityWords(allowWords);
        if (this.isSuccessResponse(response)) {
          allowCount = allowWords.length;
        } else {
          errors.push(response?.message || response?.error?.message || 'Failed to remove allowed words');
        }
      } catch (error: any) {
        errors.push(this.getErrorMessage(error, 'Failed to remove allowed words'));
      }
    }

    return {
      success: restrictCount > 0 || allowCount > 0,
      restrictCount,
      allowCount,
      errors
    };
  }

  // Get all profanity words
  getProfanityFilterWords(): Observable<vo.ProfanityFilterResponse> {
    return this.apiService.getProfanityFilterList();
  }

  // Add words (async version for component)
  async addProfanityWords(words: string[]): Promise<vo.BaseResponse> {
    const request: vo.ProfanityFilterRequest = { profanities: words };
    return await firstValueFrom(
      this.apiService.saveProfanityFilterWords(request)
    );
  }

  // Remove words (async version for component)
  async removeProfanityWords(words: string[]): Promise<vo.BaseResponse> {
    const request: vo.ProfanityFilterRequest = { profanities: words };
    return await firstValueFrom(
      this.apiService.removeProfanityFilterWords(request)
    );
  }

  // Parse textarea input into array of words
  parseWordsFromInput(input: string): string[] {
    if (!input || input.trim() === '') {
      return [];
    }
    
    return input
      .split(/[\n,]+/)
      .map(word => word.trim())
      .filter(word => word.length > 0);
  }

  // Helper method to check if response is successful
  private isSuccessResponse(response: vo.BaseResponse): boolean {
    return response && (response.status === 200 || response.status === 201);
  }

  // Helper method to extract error message
  private getErrorMessage(error: any, defaultMessage: string): string {
    return error?.error?.message 
      || error?.error?.error?.message 
      || error?.message 
      || defaultMessage;
  }
}
